// Auth Service for handling user login, registration, and XML persistence

const AUTH_KEY = 'user_session';

// Helper to fetch XML and parse it
const getAuthXml = async () => {
    try {
        const response = await fetch('/auth.xml?' + new Date().getTime());
        const xmlText = await response.text();
        const parser = new DOMParser();
        return parser.parseFromString(xmlText, "text/xml");
    } catch (e) {
        const parser = new DOMParser();
        return parser.parseFromString('<?xml version="1.0" encoding="UTF-8"?><Users></Users>', "text/xml");
    }
};

// Helper to save XML back to server using our setupProxy.js middleware
const saveAuthXml = async (xmlDoc) => {
    try {
        const serializer = new XMLSerializer();
        const xmlStr = serializer.serializeToString(xmlDoc);
        const res = await fetch('/api/auth/save', {
            method: 'POST',
            headers: { 'Content-Type': 'application/xml' },
            body: xmlStr
        });
        return res.ok;
    } catch (e) {
        console.error('Failed to save auth XML:', e);
        return false;
    }
};

// Check if user is logged in
export const isLoggedIn = () => {
    const user = localStorage.getItem(AUTH_KEY);
    return !!user;
};

// Get current user info
export const getCurrentUser = () => {
    const user = localStorage.getItem(AUTH_KEY);
    return user ? JSON.parse(user) : null;
};

// Login function (checks XML)
export const login = async (emailOrName, password) => {
    // Keep old fallback just in case
    if ((emailOrName === 'gobind' || emailOrName === 'gobind@example.com') && password === 'admin') {
        const userSession = {
            id: '1',
            username: 'Gobind',
            email: 'gobind@example.com',
            token: 'fake-jwt-token-forever'
        };
        localStorage.setItem(AUTH_KEY, JSON.stringify(userSession));
        return { success: true, user: userSession };
    }

    const xmlDoc = await getAuthXml();
    const users = xmlDoc.getElementsByTagName('User');

    for (let i = 0; i < users.length; i++) {
        const u = users[i];
        const email = u.getElementsByTagName('Email')[0]?.textContent;
        const pass = u.getElementsByTagName('Password')[0]?.textContent;
        if (email === emailOrName && pass === password) {
            const userSession = {
                id: u.getAttribute('id') || Date.now().toString(),
                username: u.getElementsByTagName('FullName')[0]?.textContent || email,
                email: email,
                phone: u.getElementsByTagName('Phone')[0]?.textContent,
                token: 'user-token'
            };
            localStorage.setItem(AUTH_KEY, JSON.stringify(userSession));
            return { success: true, user: userSession };
        }
    }
    return { success: false, message: 'Invalid credentials' };
};

// Register function
export const register = async (fullName, phone, email, password) => {
    try {
        const xmlDoc = await getAuthXml();
        const users = xmlDoc.getElementsByTagName('User');

        // Check if user exists — if so, update details
        let userNode = null;
        let userId = Date.now().toString();
        for (let i = 0; i < users.length; i++) {
            const existingEmail = users[i].getElementsByTagName('Email')[0]?.textContent;
            if (existingEmail === email) {
                userNode = users[i];
                userId = userNode.getAttribute('id') || userId;
                break;
            }
        }

        if (userNode) {
            // Update existing user
            userNode.getElementsByTagName('FullName')[0].textContent = fullName;
            userNode.getElementsByTagName('Phone')[0].textContent = phone;
            userNode.getElementsByTagName('Password')[0].textContent = password;
        } else {
            // Create new User node
            const newUser = xmlDoc.createElement('User');
            newUser.setAttribute('id', userId);
            
            const nameNode = xmlDoc.createElement('FullName');
            nameNode.textContent = fullName;
            newUser.appendChild(nameNode);
            
            const phoneNode = xmlDoc.createElement('Phone');
            phoneNode.textContent = phone;
            newUser.appendChild(phoneNode);
            
            const emailNode = xmlDoc.createElement('Email');
            emailNode.textContent = email;
            newUser.appendChild(emailNode);
            
            const passNode = xmlDoc.createElement('Password');
            passNode.textContent = password;
            newUser.appendChild(passNode);

            xmlDoc.getElementsByTagName('Users')[0].appendChild(newUser);
        }
        
        const saved = await saveAuthXml(xmlDoc);
        if (!saved) {
            return { success: false, message: 'Failed to save. Server may not be running.' };
        }

        const userSession = {
            id: userId,
            username: fullName,
            email: email,
            phone: phone,
            token: 'user-token'
        };
        localStorage.setItem(AUTH_KEY, JSON.stringify(userSession));
        
        return { success: true, user: userSession };
    } catch (e) {
        console.error('Register error:', e);
        return { success: false, message: 'Registration failed. Please try again.' };
    }
};

// Login with Google Profile
export const loginWithGoogle = async (googleUser) => {
    const xmlDoc = await getAuthXml();
    const users = xmlDoc.getElementsByTagName('User');
    
    let existingUser = null;
    for (let i = 0; i < users.length; i++) {
        const existingEmail = users[i].getElementsByTagName('Email')[0]?.textContent;
        if (existingEmail === googleUser.email) {
            existingUser = users[i];
            break;
        }
    }

    let userSession;

    if (existingUser) {
        userSession = {
            id: existingUser.getAttribute('id'),
            username: existingUser.getElementsByTagName('FullName')[0]?.textContent || googleUser.name,
            email: googleUser.email,
            picture: googleUser.picture,
            token: 'google-oauth-token'
        };
    } else {
        // Register new google user
        const newUser = xmlDoc.createElement('User');
        newUser.setAttribute('id', googleUser.sub || Date.now().toString());
        
        const nameNode = xmlDoc.createElement('FullName');
        nameNode.textContent = googleUser.name;
        newUser.appendChild(nameNode);
        
        const emailNode = xmlDoc.createElement('Email');
        emailNode.textContent = googleUser.email;
        newUser.appendChild(emailNode);
        
        const passNode = xmlDoc.createElement('Password');
        passNode.textContent = 'google-oauth';
        newUser.appendChild(passNode);

        xmlDoc.getElementsByTagName('Users')[0].appendChild(newUser);
        
        await saveAuthXml(xmlDoc);

        userSession = {
            id: googleUser.sub || Date.now().toString(),
            username: googleUser.name,
            email: googleUser.email,
            picture: googleUser.picture,
            token: 'google-oauth-token'
        };
    }

    localStorage.setItem(AUTH_KEY, JSON.stringify(userSession));
    return { success: true, user: userSession };
};

// Logout function
export const logout = () => {
    localStorage.removeItem(AUTH_KEY);
    window.location.reload(); 
};

// Address Management
const ADDRESS_KEY = 'user_addresses';

export const getAddresses = () => {
    const data = localStorage.getItem(ADDRESS_KEY);
    return data ? JSON.parse(data) : [];
};

export const addAddress = (address) => {
    let addresses = getAddresses();
    const newAddress = { ...address, id: Date.now() };
    addresses.push(newAddress);
    localStorage.setItem(ADDRESS_KEY, JSON.stringify(addresses));
    return newAddress;
};

export const removeAddress = (id) => {
    let addresses = getAddresses();
    addresses = addresses.filter(a => a.id !== id);
    localStorage.setItem(ADDRESS_KEY, JSON.stringify(addresses));
};
